import { ThemeProvider } from "@mui/material/styles";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from "react";

import { getTheme } from "./theme.js";

/**
 * Light/dark mode for the whole app, and the provider that installs the
 * matching MUI theme.
 *
 * Modelled on the customer support portal's `contexts/ThemeContext.tsx`, and
 * deliberately the same shape as it:
 *
 *   • **Dark is the default.** The XOP brand site leads with a dark hero and
 *     both portals follow it; light is an explicit opt-out, not the other way
 *     round.
 *   • **There is no "system" option.** Two states, one toggle. A legacy or
 *     corrupt stored value falls back to dark rather than to the device.
 *   • The choice persists in `localStorage`, and a private-mode browser that
 *     refuses storage still gets a working (just non-persistent) toggle.
 *
 * What is *not* copied is the mechanism: the reference is Tailwind and flips
 * a `.dark` class that its CSS variables key off. Here the mode has to drive
 * two things — the MUI theme object (which is what `sx` and every component
 * read) and the `.theme-dark` token block in `globals.css` (which is what the
 * handful of plain-CSS utilities read). So this provider does both, and is
 * the only place that knows the mode.
 */
export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "xop-any-time.theme";

/** The class `globals.css` hangs its dark token block on. */
const DARK_CLASS = "theme-dark";

interface ThemeModeValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeValue | undefined>(undefined);

/** Read the persisted choice. Anything that is not an explicit `"light"` —
 * unset, corrupt, or a throwing `localStorage` — means dark. */
export function readStoredMode(): ThemeMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export function ThemeModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);

  useEffect(() => {
    document.documentElement.classList.toggle(DARK_CLASS, mode === "dark");
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* Private mode: the class is applied, the choice just won't survive a
         reload. Losing a preference is not worth a thrown render. */
    }
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);
  // `getTheme` builds a fresh MUI theme (~1ms); memoise so a re-render of any
  // consumer doesn't rebuild it and invalidate every emotion cache below.
  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode must be used inside <ThemeModeProvider>");
  return ctx;
}
